import { TimePointV1 } from '../calendar/time-point.js';
import { CanonStatus, ChronEdge, Library, LinkOrigin, MentionKind, SpanStatus, Tick, } from '../domain/enums.js';
import { z } from '../zod.js';
import { Fid, IsoTimestamp, RowVersion, Uuid } from './common.js';
import { DocTitle, JsonObject, TypeKey } from './documents.js';
/**
 * A link type's key: lower case, digits and underscores, as the built-in keys are (`LINK_TYPE_KEYS`
 * names those fourteen; the GM may add others).
 */
export const LinkTypeKeyText = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
/**
 * A link type's source or target rule (baseline section 3: advisory, warn only). The seeds hold
 * `{}`; a key 1.C stage 1 needs is added here as an optional key (by this package before its
 * stage 3, by CS after). Any other key is refused.
 */
export const LinkRuleV1 = z.strictObject({});
/** A key of a link's `meta`. */
export const MetaKey = z.string().regex(/^[a-z][a-zA-Z0-9_]{0,63}$/);
/**
 * The JSON Schema of a link type's `meta` (1.0a decision 11): an object schema that admits no
 * other property, whose `properties` map each meta key to a JSON Schema object and whose
 * `required` names some of those keys.
 */
export const LinkMetaSchemaV1 = z
    .strictObject({
    type: z.literal('object'),
    properties: z.record(MetaKey, z.record(z.string(), z.unknown())),
    required: z.array(MetaKey),
    additionalProperties: z.literal(false),
})
    .refine((schema) => schema.required.every((key) => Object.hasOwn(schema.properties, key)), {
    message: 'every required key is a property',
    path: ['required'],
});
/**
 * A link type (1.0b deliverable 16a), frozen with the shapes migration 0005 seeds. `archived` is
 * from `archived_at`; `inverseLabel` is null when the type has none.
 */
export const LinkTypeV1 = z.strictObject({
    id: Uuid,
    key: LinkTypeKeyText,
    label: z.string().min(1).max(200),
    inverseLabel: z.string().min(1).max(200).nullable(),
    sourceRule: LinkRuleV1,
    targetRule: LinkRuleV1,
    ordered: z.boolean(),
    symmetric: z.boolean(),
    structural: z.boolean(),
    temporal: z.boolean(),
    metaSchema: LinkMetaSchemaV1,
    builtin: z.boolean(),
    archived: z.boolean(),
});
/**
 * The exit-1a link shapes (1.0b deliverable 18), built by 1.C stage 1. The validity fields of
 * `LinkV1` exist now and are filled from 1.C's stage 2: until then a link is valid without limit.
 */
/** `links.createType`. No key: the server derives it from the label. A create appends. */
export const LinkTypeCreateRequestV1 = z.strictObject({
    label: z.string().min(1).max(200),
    inverseLabel: z.string().min(1).max(200).optional(),
    ordered: z.boolean().optional(),
    symmetric: z.boolean().optional(),
    structural: z.boolean().optional(),
    temporal: z.boolean().optional(),
    metaSchema: LinkMetaSchemaV1.optional(),
});
/** Which links of a document a list reads: those out of it, or those into it. */
export const LINK_DIRECTIONS = ['out', 'in'];
export const LinkDirection = z.enum(LINK_DIRECTIONS);
/**
 * A tick range (`int8range`): `lo` inclusive, `hi` exclusive, null where unbounded; or empty, an
 * impossible window (baseline section 3, "Time-bounded links"). Placements and states use it too.
 */
export const TickSpanV1 = z.discriminatedUnion('empty', [
    z.strictObject({ empty: z.literal(true) }),
    z.strictObject({ empty: z.literal(false), lo: Tick.nullable(), hi: Tick.nullable() }),
]);
/** One end of a link's validity: an authored time point, or an edge of a placement. */
export const LinkBoundaryV1 = z.union([
    z.strictObject({ timePoint: TimePointV1 }),
    z.strictObject({ placementId: Uuid, edge: ChronEdge }),
]);
const LinkText = z.string().min(1).max(500);
/** A link (`link`), its validity included; `rv` guards every write to it. */
export const LinkV1 = z.strictObject({
    id: Uuid,
    rv: RowVersion,
    linkTypeId: Uuid,
    sourceId: Uuid,
    targetId: Uuid,
    position: z.string().min(1).max(200).nullable(),
    label: LinkText.nullable(),
    role: LinkText.nullable(),
    note: z.string().max(10_000).nullable(),
    meta: JsonObject,
    isPrimary: z.boolean(),
    origin: LinkOrigin,
    /** Provenance only: the field the link was written through. */
    fieldDefId: Uuid.nullable(),
    /** Null: unbounded on that side. */
    validFrom: LinkBoundaryV1.nullable(),
    validTo: LinkBoundaryV1.nullable(),
    ongoing: z.boolean(),
    certainSpan: TickSpanV1,
    possibleSpan: TickSpanV1,
    spanStatus: SpanStatus,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/**
 * An ongoing link has no end (CS r3, deliverable 72): `ongoing: true` beside a `validTo` that is
 * not null is refused. What the stored row already holds is the server's check (1.C stage 2).
 */
function ongoingHasNoEnd(body, ctx) {
    if (body.ongoing === true && body.validTo !== undefined && body.validTo !== null) {
        ctx.addIssue({ code: 'custom', message: 'an ongoing link has no end', path: ['ongoing'] });
    }
}
/**
 * `links.create`, from the document of the path. The link type is named by key (a built-in key,
 * or the key of one the GM made); `afterLinkId` places it in an ordered type (null: first). From
 * CS r3 (1.C stage 2) it may carry its validity: `validFrom` and `validTo` (a time point or a
 * placement edge; null or absent, unbounded) and `ongoing`, as `StateCreateV1` carries `from`
 * and `to`. A boundary unknown, trashed or refused answers 422 `invalid_body`.
 */
export const LinkCreateV1 = z
    .strictObject({
    linkTypeKey: LinkTypeKeyText,
    targetId: Uuid,
    isPrimary: z.boolean().optional(),
    label: LinkText.optional(),
    role: LinkText.optional(),
    note: z.string().max(10_000).optional(),
    meta: JsonObject.optional(),
    afterLinkId: Uuid.nullable().optional(),
    validFrom: LinkBoundaryV1.nullable().optional(),
    validTo: LinkBoundaryV1.nullable().optional(),
    ongoing: z.boolean().optional(),
})
    .superRefine(ongoingHasNoEnd);
/**
 * `links.update`: the row version it was based on, then any of the link's own text and meta, and
 * `isPrimary` (CS r1c): true makes the source its target's home, false clears that home. From CS
 * r3, any of its validity: a boundary sets that side, null clears it, absence leaves it.
 */
export const LinkUpdateV1 = z
    .strictObject({
    baseRv: RowVersion,
    label: LinkText.nullable().optional(),
    role: LinkText.nullable().optional(),
    note: z.string().max(10_000).nullable().optional(),
    meta: JsonObject.optional(),
    isPrimary: z.boolean().optional(),
    validFrom: LinkBoundaryV1.nullable().optional(),
    validTo: LinkBoundaryV1.nullable().optional(),
    ongoing: z.boolean().optional(),
})
    .superRefine(ongoingHasNoEnd);
/** `links.reorder`: move one link after another of its document (null: first). */
export const LinkReorderV1 = z.strictObject({
    linkId: Uuid,
    afterLinkId: Uuid.nullable(),
    baseRv: RowVersion,
});
/** `links.list`: both directions and every type when omitted. */
export const LinksQueryV1 = z.strictObject({
    direction: LinkDirection.optional(),
    linkTypeKey: LinkTypeKeyText.optional(),
});
/** `/api/docs/:id/links/:linkId`. */
export const LinkParams = z.strictObject({ id: Uuid, linkId: Uuid });
/** What the lint found among a document's links (baseline section 3, "Time-bounded links"). */
export const LINK_WARNING_KINDS = ['duplicate', 'overlaps', 'may_overlap'];
export const LinkWarningKind = z.enum(LINK_WARNING_KINDS);
/** What a lint warning offers to do about it. */
export const LINK_WARNING_OFFERS = ['extend_existing', 'merge', 'keep_both'];
export const LinkWarningOffer = z.enum(LINK_WARNING_OFFERS);
/** Links of one source, target, type and role that duplicate or (may) overlap. */
export const LinkWarningV1 = z.strictObject({
    kind: LinkWarningKind,
    linkIds: z.array(Uuid).min(2),
    offers: z.array(LinkWarningOffer).min(1),
});
/** `links.create`'s answer: the link, the primary link it demoted (if any), and the lint. */
export const LinkCreateResultV1 = z.strictObject({
    link: LinkV1,
    demoted: LinkV1.nullable(),
    warnings: z.array(LinkWarningV1),
});
/** A document named in a link answer: enough to draw its chip. */
export const DocRefV1 = z.strictObject({
    id: Uuid,
    fid: Fid,
    title: DocTitle,
    titlePending: z.boolean(),
    typeKey: TypeKey,
    library: Library,
    isStub: z.boolean(),
});
/**
 * A document's backlinks (the rail's Backlinks tab): the links into it (and symmetric links either
 * way) with the document at the other end; the mentions of it with their context; and, for a
 * reference page, the canon documents that use it and its variants (`variant_of`), grouped as
 * "Used in" and "Variants" (A2.1 section 4). On a canon page the two groups are empty.
 */
export const BacklinksV1 = z.strictObject({
    relations: z.array(z.strictObject({ link: LinkV1, other: DocRefV1 })),
    mentions: z.array(z.strictObject({
        source: DocRefV1,
        blockId: Uuid,
        kind: MentionKind,
        label: z.string(),
        context: z.string(),
        inSecret: z.boolean(),
        inGmNote: z.boolean(),
    })),
    usedIn: z.array(DocRefV1),
    variants: z.array(DocRefV1),
});
/** `links.tree`: the children of one parent (the top level when omitted). */
export const TreeQueryV1 = z.strictObject({ parentId: Uuid.optional() });
/** A row of the story tree: a document under its primary parent, with its child count. */
export const TreeNodeV1 = z.strictObject({
    ...DocRefV1.shape,
    canonStatus: CanonStatus,
    /** The primary `contains` link that homes it; null at the top level. */
    linkId: Uuid.nullable(),
    position: z.string().min(1).max(200).nullable(),
    childCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=links.js.map